<template>
  <v-dialog :value="value" max-width="1200" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-3">mdi-file-clock-outline</v-icon>
        {{ $t("ged_historico_titulo") }}
        <v-spacer />
        <v-btn icon color="primary" :aria-label="$t('Fechar')" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle>{{ documento && documento.descricao }}</v-card-subtitle>
      <v-card-text>
        <v-alert v-if="erro" type="error" dense outlined>{{ erro }}</v-alert>
        <v-alert v-if="sucesso" type="success" dense outlined>{{ sucesso }}</v-alert>

        <v-card v-if="!documento || !documento.excluido" outlined class="pa-4 mb-6">
          <div class="subtitle-1 font-weight-bold mb-2">{{ $t("ged_nova_versao") }}</div>
          <p class="body-2 mb-3">{{ $t("ged_nova_versao_ajuda") }}</p>
          <div class="d-flex align-center flex-wrap">
            <v-file-input
              v-model="arquivo"
              :label="$t('ged_selecionar_pdf')"
              accept=".pdf,application/pdf"
              outlined dense hide-details class="mr-4"
              style="max-width: 420px"
              :disabled="salvando"
            />
            <v-btn
              v-can="'ROLE_FuncionarioGed_Update'"
              class="ged-version-button font-weight-bold text-none btn-primary bg-gradient-primary px-6"
              height="39"
              :disabled="!arquivo || salvando"
              :loading="salvando"
              @click="substituir"
            >
              <v-icon left>mdi-upload</v-icon>{{ $t("ged_guardar_versao") }}
            </v-btn>
          </div>
        </v-card>

        <h3 class="subtitle-1 mb-3">{{ $t("ged_versoes") }}</h3>
        <v-progress-linear v-if="carregando" indeterminate color="primary" class="mb-3" />
        <v-simple-table v-else dense class="mb-6">
          <thead>
            <tr>
              <th>{{ $t("ged_versao") }}</th>
              <th>{{ $t("Data") }}</th>
              <th>{{ $t("Responsavel") }}</th>
              <th>{{ $t("ged_integridade") }}</th>
              <th>{{ $t("Acoes") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="versao in versoes" :key="versao.id">
              <td><strong>v{{ versao.numero }}</strong></td>
              <td>{{ formatarData(versao.instante) }}</td>
              <td>{{ formatarResponsavel(versao) }}</td>
              <td>{{ versao.sha256 ? $t("ged_hash_registrado") : $t("ged_hash_pendente") }}</td>
              <td>
                <v-btn small outlined color="primary" class="text-none font-weight-bold"
                  :loading="baixando === versao.numero"
                  @click="baixarVersao(versao)">
                  <v-icon left small>mdi-download</v-icon>{{ $t("Baixar") }}
                </v-btn>
              </td>
            </tr>
            <tr v-if="!versoes.length"><td colspan="5">{{ $t("ged_sem_versoes") }}</td></tr>
          </tbody>
        </v-simple-table>

        <h3 class="subtitle-1 mb-3">{{ $t("ged_acessos") }}</h3>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>{{ $t("Data") }}</th>
              <th>{{ $t("Responsavel") }}</th>
              <th>{{ $t("ged_acao") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="acesso in acessos" :key="acesso.id">
              <td>{{ formatarData(acesso.instante) }}</td>
              <td>{{ formatarResponsavel(acesso) }}</td>
              <td>{{ formatarAcao(acesso.acao) }}</td>
            </tr>
            <tr v-if="!acessos.length"><td colspan="3">{{ $t("ged_sem_acessos") }}</td></tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
      <GedHistoricoPaginacao
        :page="pagina"
        :page-size="tamanhoPagina"
        :total-pages="totalPaginas"
        :loading="carregando"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
      <v-card-actions class="crud-form-actions">
        <v-btn class="font-weight-bold text-capitalize btn-ls btn-secondary bg-gradient-light px-6"
          height="39" @click="$emit('input', false)">
          {{ $t("Fechar") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FuncionarioGedDataService from "@/services/pessoal/FuncionarioGedDataService";
import GedHistoricoPaginacao from "@/components/base/Crud/GedHistoricoPaginacao.vue";

export default {
  name: "FuncionarioGedHistorico",
  components: { GedHistoricoPaginacao },
  props: {
    value: { type: Boolean, default: false },
    documento: { type: Object, default: null },
  },
  data: () => ({
    arquivo: null,
    versoes: [],
    acessos: [],
    pagina: 1,
    tamanhoPagina: 8,
    totalPaginas: 0,
    carregando: false,
    salvando: false,
    baixando: null,
    erro: "",
    sucesso: "",
  }),
  watch: {
    value(aberto) {
      if (aberto && this.documento) {
        this.pagina = 1;
        this.versoes = [];
        this.acessos = [];
        this.totalPaginas = 0;
        this.sucesso = "";
        this.carregar();
      }
    },
  },
  methods: {
    formatarResponsavel(registro) {
      return registro.usuarioEmail || (registro.usuarioId == null
        ? this.$t("Not registered")
        : `#${registro.usuarioId}`);
    },
    formatarData(valor) {
      return valor ? new Date(valor).toLocaleString("pt-BR") : "—";
    },
    formatarAcao(acao) {
      if (acao?.startsWith("DOWNLOAD_VERSAO_")) {
        return `${this.$t("ged_acao_download_versao")} ${acao.slice(16)}`;
      }
      const chaves = {
        INCLUSAO: "ged_acao_inclusao",
        NOVA_VERSAO: "ged_acao_nova_versao",
        ALTERACAO_DADOS: "ged_acao_alteracao_dados",
        EXCLUSAO_LOGICA: "ged_acao_arquivamento",
        RESTAURACAO: "ged_acao_restauracao",
        DOWNLOAD: "ged_acao_download",
        DOWNLOAD_PORTAL: "ged_acao_download_portal",
        BASELINE_INTEGRIDADE: "ged_acao_hash_inicial",
        ACESSO_NEGADO: "ged_acao_acesso_negado",
        FALHA_INTEGRIDADE: "ged_acao_falha_integridade",
        REGISTRO_LEGADO: "ged_acao_registro_legado",
      };
      return chaves[acao] ? this.$t(chaves[acao]) : acao;
    },
    async carregar(recarregarVersoes = true) {
      this.carregando = true;
      this.erro = "";
      try {
        const [versoes, pagina] = await Promise.all([
          recarregarVersoes
            ? FuncionarioGedDataService.versoes(this.documento.id)
            : Promise.resolve(this.versoes),
          FuncionarioGedDataService.acessos(
            this.documento.id, this.pagina - 1, this.tamanhoPagina
          ),
        ]);
        if (recarregarVersoes) this.versoes = versoes;
        this.acessos = pagina.content || [];
        this.totalPaginas = pagina.totalPages || 0;
      } catch (error) {
        this.erro = this.$t("ged_erro_carregar");
      } finally {
        this.carregando = false;
      }
    },
    async handlePageChange(pagina) {
      this.pagina = pagina;
      await this.carregar(false);
    },
    async handlePageSizeChange(tamanho) {
      this.tamanhoPagina = tamanho;
      this.pagina = 1;
      await this.carregar(false);
    },
    async substituir() {
      if (!this.arquivo || this.arquivo.size > 3145728) {
        this.erro = this.$t("ged_pdf_limite");
        return;
      }
      this.salvando = true;
      this.erro = "";
      this.sucesso = "";
      try {
        await FuncionarioGedDataService.substituir(this.documento.id, this.arquivo);
        this.arquivo = null;
        this.pagina = 1;
        this.sucesso = this.$t("ged_versao_guardada");
        await this.carregar();
        this.$emit("updated");
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t("ged_erro_substituir");
      } finally {
        this.salvando = false;
      }
    },
    async baixarVersao(versao) {
      this.baixando = versao.numero;
      this.erro = "";
      try {
        const response = await FuncionarioGedDataService.baixar(
          this.documento.id, false, versao.numero
        );
        FuncionarioGedDataService.salvarDownload(response, versao.nomeArquivo);
        await this.carregar(false);
      } catch (error) {
        this.erro = this.$t("ged_erro_baixar");
      } finally {
        this.baixando = null;
      }
    },
  },
};
</script>

<style scoped>
.ged-version-button.v-btn--disabled {
  background-image: none !important;
  background-color: #e9edf2 !important;
  color: #526079 !important;
}

.ged-version-button.v-btn--disabled .v-icon {
  color: #526079 !important;
}
</style>
